import { useState, useEffect, useRef } from 'react';
import './MultiSelectDropdown.css';

/**
 * Multi-select filter as a styled dropdown (checkbox list).
 * Matches ToolbarFilterDropdown shell/trigger look; avoids native multi-select UX.
 */
export default function MultiSelectDropdown({
  id,
  label,
  placeholder = 'All',
  options,
  value,
  onChange,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  const selected = value || [];

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = e => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  const toggleValue = v => {
    const next = selected.includes(v) ? selected.filter(x => x !== v) : [...selected, v];
    onChange(next);
  };

  const summary = () => {
    if (!selected.length) return placeholder;
    if (selected.length === 1) {
      const opt = options.find(o => o.value === selected[0]);
      return opt?.label ?? selected[0];
    }
    return `${selected.length} selected`;
  };

  const active = selected.length > 0;

  return (
    <div ref={rootRef} className={`msd-field${active ? ' msd-field--active' : ''}${disabled ? ' msd-field--disabled' : ''}`}>
      {label ? (
        <label className="msd-label" htmlFor={`${id}-trigger`}>
          {label}
        </label>
      ) : null}
      <div className={`msd-shell${open ? ' msd-shell--open' : ''}`}>
        <button
          type="button"
          id={`${id}-trigger`}
          className="msd-trigger"
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={`${id}-panel`}
          disabled={disabled}
          onClick={() => !disabled && setOpen(o => !o)}
          onKeyDown={e => {
            if (e.key === 'Escape' && open) {
              e.preventDefault();
              setOpen(false);
            }
          }}
        >
          <span className="msd-value">{summary()}</span>
          {active ? <span className="msd-count">{selected.length}</span> : null}
        </button>
        {open && !disabled ? (
          <div
            id={`${id}-panel`}
            className="msd-panel"
            role="listbox"
            aria-multiselectable="true"
            aria-label={label || placeholder}
          >
            {options.length === 0 ? (
              <div className="msd-empty">No options</div>
            ) : (
              options.map(opt => {
                const checked = selected.includes(opt.value);
                return (
                  <label key={String(opt.value)} className={`msd-option${checked ? ' msd-option--checked' : ''}`}>
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleValue(opt.value)}
                    />
                    <span className="msd-option-text">{opt.label}</span>
                  </label>
                );
              })
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}
